/**
 * Phase 2 — WebAuthn client helper.
 *
 * Thin layer on top of navigator.credentials that handles the
 * ArrayBuffer ↔ base64url plumbing the WebAuthn spec requires (the
 * browser hands us binary blobs; we need to ship them as JSON to the
 * backend, and the backend echoes JSON back containing base64url-encoded
 * binary fields that the browser then needs as ArrayBuffers).
 *
 * Public API:
 *   isSupported()       → boolean
 *   enrolPasskey(opts)  → completes the registration ceremony, returns
 *                         the JSON the backend's /webauthn/register/finish
 *                         expects.
 *   loginPasskey(opts)  → completes the authentication ceremony, returns
 *                         the JSON the backend's /webauthn/auth/finish
 *                         expects.
 *
 * Both helpers leave the network calls to the caller — the page knows
 * its own auth flow, success/failure handling and toast plumbing.
 */

/** True if this browser/runtime exposes the WebAuthn API at all. */
export function isSupported() {
  return typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && typeof navigator !== 'undefined'
    && !!navigator.credentials
    && typeof navigator.credentials.create === 'function'
    && typeof navigator.credentials.get === 'function';
}

// ─── Encoding helpers ────────────────────────────────────────────────

/** ArrayBuffer / Uint8Array → base64url string (no padding). */
export function bufToB64u(buf) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** base64url string → ArrayBuffer. */
export function b64uToBuf(b64u) {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((b64u.length + 3) % 4);
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

/** Walk a publicKey options dict from the server and turn its base64url
 * fields into ArrayBuffers so navigator.credentials accepts them. */
function decodePublicKeyOptions(opts) {
  const out = { ...opts };
  if (out.challenge) out.challenge = b64uToBuf(out.challenge);
  if (out.user && out.user.id) out.user = { ...out.user, id: b64uToBuf(out.user.id) };
  if (Array.isArray(out.excludeCredentials)) {
    out.excludeCredentials = out.excludeCredentials.map((c) => ({ ...c, id: b64uToBuf(c.id) }));
  }
  if (Array.isArray(out.allowCredentials)) {
    out.allowCredentials = out.allowCredentials.map((c) => ({ ...c, id: b64uToBuf(c.id) }));
  }
  return out;
}

/** Encode a PublicKeyCredential (the browser's response) into JSON the
 * backend can verify. py-webauthn 2.x accepts this shape directly. */
function encodeRegistrationCredential(cred) {
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64u(cred.response.clientDataJSON),
      attestationObject: bufToB64u(cred.response.attestationObject),
      transports: cred.response.getTransports ? cred.response.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    authenticatorAttachment: cred.authenticatorAttachment || null,
  };
}

function encodeAuthenticationCredential(cred) {
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64u(cred.response.clientDataJSON),
      authenticatorData: bufToB64u(cred.response.authenticatorData),
      signature: bufToB64u(cred.response.signature),
      userHandle: cred.response.userHandle ? bufToB64u(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    authenticatorAttachment: cred.authenticatorAttachment || null,
  };
}

// ─── Public ceremony helpers ─────────────────────────────────────────

/**
 * Run the registration ceremony.
 *
 * @param {Object} publicKey – server's response from /webauthn/register/begin
 *                              (the `publicKey` field already unwrapped).
 * @returns the encoded credential dict to POST to /webauthn/register/finish.
 * @throws if the user cancels, no authenticator is available, or the
 *         browser refuses (e.g. excluded credential collision).
 */
export async function enrolPasskey(publicKey) {
  if (!isSupported()) throw new Error('Passkeys are not supported in this browser');
  const decoded = decodePublicKeyOptions(publicKey);
  const cred = await navigator.credentials.create({ publicKey: decoded });
  if (!cred) throw new Error('Authenticator returned no credential');
  return encodeRegistrationCredential(cred);
}

/**
 * Run the authentication ceremony.
 *
 * @param {Object} publicKey – server's response from /webauthn/auth/begin.
 * @returns the encoded assertion dict to POST to /webauthn/auth/finish.
 * @throws if no credential is selected, the user cancels, or the
 *         browser refuses.
 */
export async function loginPasskey(publicKey) {
  if (!isSupported()) throw new Error('Passkeys are not supported in this browser');
  const decoded = decodePublicKeyOptions(publicKey);
  const cred = await navigator.credentials.get({ publicKey: decoded });
  if (!cred) throw new Error('Authenticator returned no credential');
  return encodeAuthenticationCredential(cred);
}

/** Best-effort feature detection for conditional UI (autofill-style
 * passkey suggestions). Used to decide whether to even render the
 * "sign in with passkey" button. */
export async function isConditionalMediationAvailable() {
  if (!isSupported() || !window.PublicKeyCredential?.isConditionalMediationAvailable) {
    return false;
  }
  try {
    return await window.PublicKeyCredential.isConditionalMediationAvailable();
  } catch {
    return false;
  }
}
